import React, { useState, useEffect } from 'react';
import { addCourseGuide, updateCourseGuide, deleteCourseGuide, getAllCourseGuides } from '../services/courseGuidesService';

function AdminCourseGuidesModal({ isOpen, onClose, course, existingGuides, onGuidesUpdated }) {
  const [guideName, setGuideName] = useState('');
  const [customGuideName, setCustomGuideName] = useState('');
  const [guideLink, setGuideLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingGuide, setEditingGuide] = useState(null);
  const [allGuides, setAllGuides] = useState([]);
  const [uniqueGuideOptions, setUniqueGuideOptions] = useState([]);

  // Fetch all guides to get unique guide names and their links
  useEffect(() => {
    if (isOpen) {
      getAllCourseGuides().then(guides => {
        setAllGuides(guides);
        // Get unique guide names
        const uniqueNames = [...new Set(guides.map(g => g.guide))].sort();
        setUniqueGuideOptions(uniqueNames);
      }).catch(err => console.error('Error fetching guides:', err));
    }
  }, [isOpen]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setGuideName('');
      setCustomGuideName('');
      setGuideLink('');
      setError('');
      setEditingGuide(null);
    }
  }, [isOpen]);

  // Auto-fill link when selecting an existing guide
  useEffect(() => {
    if (guideName && guideName !== 'Other' && allGuides.length > 0) {
      const matchingGuide = allGuides.find(g => g.guide === guideName);
      if (matchingGuide) {
        setGuideLink(matchingGuide.link);
      }
    }
  }, [guideName, allGuides]);

  // When editing, populate the form
  useEffect(() => {
    if (editingGuide) {
      const name = editingGuide.guide || '';
      // Check if the guide name exists in the unique options from the database
      if (uniqueGuideOptions.includes(name)) {
        setGuideName(name);
        setCustomGuideName('');
      } else {
        setGuideName('Other');
        setCustomGuideName(name);
      }
      setGuideLink(editingGuide.link || '');
    }
  }, [editingGuide, uniqueGuideOptions]);

  if (!isOpen || !course) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const finalGuideName = guideName === 'Other' ? customGuideName : guideName;

    if (!finalGuideName || !guideLink) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    try {
      if (editingGuide) {
        await updateCourseGuide(editingGuide.id, {
          guide: finalGuideName,
          link: guideLink
        });
      } else {
        await addCourseGuide({
          courseId: course.course_id,
          courseName: course.name,
          guide: finalGuideName,
          link: guideLink
        });
      }

      // Notify parent to refresh guides list - await to ensure state updates before form resets
      if (onGuidesUpdated) {
        await onGuidesUpdated();
      }

      // Reset form
      setGuideName('');
      setCustomGuideName('');
      setGuideLink('');
      setEditingGuide(null);
      setLoading(false);
    } catch (err) {
      console.error('Error saving guide:', err);
      setError('Failed to save guide. Please try again.');
      setLoading(false);
    }
  };

  const handleDelete = async (guideId) => {
    if (!window.confirm('Are you sure you want to remove this course from the guide?')) {
      return;
    }

    setLoading(true);
    try {
      await deleteCourseGuide(guideId);
      if (onGuidesUpdated) {
        await onGuidesUpdated();
      }
      setLoading(false);
    } catch (err) {
      console.error('Error deleting guide:', err);
      setError('Failed to delete guide. Please try again.');
      setLoading(false);
    }
  };

  const handleEdit = (guide) => {
    setEditingGuide(guide);
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingGuide(null);
    setGuideName('');
    setCustomGuideName('');
    setGuideLink('');
    setError('');
  };

  return (
    <div className="modal-overlay" style={{
      zIndex: 3000,
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.3)'
    }} onClick={onClose}>
      <div className="modal-content" style={{
        maxWidth: 500,
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
        margin: '40px auto',
        padding: '24px'
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, color: '#1D665D' }}>
            {editingGuide ? 'Edit Guide Link' : 'Add to Guide'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666'
            }}
          >
            x
          </button>
        </div>

        {/* Course Info */}
        <div style={{
          background: '#f5f5f5',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 20
        }}>
          <div style={{ fontWeight: 600, color: '#333' }}>{course.name}</div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {course.city}{course.city && course.state ? ', ' : ''}{course.state}
          </div>
        </div>

        {/* Add/Edit Form */}
        <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
              Guide Name *
            </label>
            <select
              value={guideName}
              onChange={e => {
                setGuideName(e.target.value);
                if (e.target.value === 'Other') {
                  setGuideLink('');
                }
              }}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 14,
                boxSizing: 'border-box',
                background: '#fff'
              }}
            >
              <option value="">Select a guide...</option>
              {uniqueGuideOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
              <option value="Other">+ Create New Guide</option>
            </select>
          </div>

          {guideName === 'Other' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                  New Guide Name *
                </label>
                <input
                  type="text"
                  value={customGuideName}
                  onChange={e => setCustomGuideName(e.target.value)}
                  placeholder="Enter guide name"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                  Guide Link *
                </label>
                <input
                  type="url"
                  value={guideLink}
                  onChange={e => setGuideLink(e.target.value)}
                  placeholder="https://golfnear.me/article/... or Instagram link"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </>
          )}

          {guideName && guideName !== 'Other' && guideLink && (
            <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
              <span style={{ fontSize: 12, color: '#666' }}>Link: </span>
              <span style={{ fontSize: 12, color: '#1D665D' }}>{guideLink}</span>
            </div>
          )}

          {error && (
            <div style={{ color: '#dc2626', marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px 20px',
                borderRadius: 8,
                border: 'none',
                background: '#1D665D',
                color: '#fff',
                fontWeight: 600,
                fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : editingGuide ? 'Update Guide' : 'Add to Guide'}
            </button>
            {editingGuide && (
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={loading}
                style={{
                  padding: '12px 20px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  background: '#fff',
                  color: '#666',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* Existing Guides List */}
        {existingGuides && existingGuides.length > 0 && (
          <div>
            <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 16 }}>
              This Course Appears In ({existingGuides.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {existingGuides.map(guide => (
                <div
                  key={guide.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: editingGuide?.id === guide.id ? '#e4f8ec' : '#f9f9f9',
                    borderRadius: 8,
                    border: editingGuide?.id === guide.id ? '2px solid #1D665D' : '1px solid #eee'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 500,
                      color: '#1D665D',
                      fontSize: 14,
                      marginBottom: 4
                    }}>
                      {guide.guide}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {guide.link}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                    <button
                      onClick={() => handleEdit(guide)}
                      disabled={loading}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid #1D665D',
                        background: '#fff',
                        color: '#1D665D',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(guide.id)}
                      disabled={loading}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid #dc2626',
                        background: '#fff',
                        color: '#dc2626',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminCourseGuidesModal;
